/**
 * The single knob for the SPA's URL prefix.
 *
 * Vite populates `import.meta.env.BASE_URL` from `base` in `vite.config.mts`
 * (currently the app prefix). Unlike `process.env.BASE_URL` it survives the
 * `define: { "process.env": {} }` in that same config, so it is the one value
 * that is actually populated at runtime.
 *
 * vue-router is handed this value as its history base, which means:
 *
 * - Route records and every router-mediated destination (`push`/`replace`,
 *   `to`, record `redirect`, `meta`, guard returns) are written
 *   **base-relative**: `/home`, with no prefix. The router re-applies the base
 *   when it generates an `href`.
 * - Paths the router hands back (`route.path`, `route.fullPath`, `to.path`)
 *   already have the base stripped, so comparisons against them are
 *   base-relative too.
 *
 * Navigation that goes *around* the router — a native `<a href>`, a
 * `window.location` assignment, an absolute `${origin}/...` URL — gets nothing
 * re-applied for it, so it must be built through {@link appHref} or
 * {@link appUrl}.
 */
export const APP_BASE_URL: string = import.meta.env.BASE_URL || "/";

/** The base without its trailing slash, so joining a leading-slash path is exact. */
const APP_BASE_NO_TRAILING_SLASH = APP_BASE_URL.endsWith("/") ? APP_BASE_URL.slice(0, -1) : APP_BASE_URL;

/** The legacy SPA prefix, kept as a permanent compatibility surface (see {@link stripLegacyAppPrefix}). */
const LEGACY_APP_PREFIX = "/app";

function withLeadingSlash(path: string): string {
  return path.startsWith("/") ? path : `/${path}`;
}

/**
 * Root-relative URL for a base-relative app path, for navigation the browser
 * resolves itself (native `href`, `window.location`), which vue-router never
 * sees and therefore never prefixes.
 */
export function appHref(path = "/"): string {
  return `${APP_BASE_NO_TRAILING_SLASH}${withLeadingSlash(path)}`;
}

/**
 * Absolute, origin-qualified URL for a base-relative app path. Use for links
 * that leave the app — share URLs, QR codes, Stripe return URLs, emails.
 */
export function appUrl(path = "/", origin: string = window.location.origin): string {
  return `${origin}${appHref(path)}`;
}

/**
 * Strip the legacy SPA prefix from a path, on a segment boundary only, so
 * `/appointments` is left alone.
 *
 * `?redirect=` values carrying the prefix are already sitting in delivered
 * emails and bookmarks; no deploy ordering reaches them. Stripping where the
 * value is *consumed* keeps them resolving against base-relative route records.
 */
export function stripLegacyAppPrefix(path: string): string {
  if (path === LEGACY_APP_PREFIX) return "/";
  if (path.startsWith(`${LEGACY_APP_PREFIX}/`)) return path.slice(LEGACY_APP_PREFIX.length);
  return path;
}
